import { OptionSelect } from '@/core/utils/types'
import * as z from 'zod'
import { MOVER_ITEM } from '../data/mover-item'
import { ITEMS_POR_PASTA_QUERY } from '../data/items-pasta-query'
import { useSnackbar } from '@tecsinapse/cortex-react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { plural, t } from '@lingui/core/macro'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  ItemBiblioteca,
  MoverItemMutationMutation,
  MoverItemMutationMutationVariables
} from '@/core/types/graphql'
import { request } from '../../../core/utils/request'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface MoverValues {
  novaLocalizacao: OptionSelect
}

export const useMoverDialog = ({
  items,
  close
}: {
  items: ItemBiblioteca[]
  close: () => void
}) => {
  const client = useQueryClient()

  const moverItemMutation = useMutation({
    mutationKey: ['moverItem'],
    mutationFn: async ({
      variables
    }: {
      variables: MoverItemMutationMutationVariables
    }) =>
      await request<
        MoverItemMutationMutation,
        MoverItemMutationMutationVariables
      >(MOVER_ITEM, { variables }),
    onSuccess: async () => {
      await client.refetchQueries({ queryKey: ['itemsPorPasta'] })
      await client.refetchQueries({ queryKey: ['buscaBibliotecaPaged'] })
    }
  })

  const { snackbar } = useSnackbar()

  const form = useForm<MoverValues>({
    defaultValues: { novaLocalizacao: {} as OptionSelect },
    resolver: zodResolver(
      z.object({
        novaLocalizacao: z.object(
          { value: z.string(), label: z.string() },
          { error: t`O campo Nova Localização é obrigatório` }
        )
      })
    ),
    mode: 'onSubmit'
  })

  const onSubmit: SubmitHandler<MoverValues> = async values => {
    try {
      await Promise.all(
        items.map(item =>
          moverItemMutation.mutateAsync({
            variables: {
              idItemBiblioteca: item.id,
              novaLocalizacao: values?.novaLocalizacao.value
            }
          })
        )
      )

      snackbar.show(
        'success',
        t({
          id: 'msg.plural',
          message: plural(items.length, {
            one: 'Item movido com sucesso!',
            other: 'Itens movidos com sucesso!'
          })
        })
      )

      form.reset()
      close()
    } catch {
      snackbar.show('error', t`Erro ao mover`)
    }
  }

  const closeForm = () => {
    form.reset()
    close()
  }

  return {
    form,
    onSubmit,
    closeForm
  }
}
